import { useInfiniteQuery } from "@tanstack/react-query";
import {
  tenantService,
  TenantFilters,
  TenantsResponse,
} from "../api/services/tenantService";
import type { Tenant } from "../schemas/tenantSchema";
import { useTenants } from "./useTenants";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type TenantsInfiniteFilters = Omit<TenantFilters, "page" | "pageSize"> & {
  pageSize?: number;
};

export const useTenantsInfinite = (filters: TenantsInfiniteFilters = {}) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;
  const mutations = useTenants(undefined, { enabled: false });

  const query = useInfiniteQuery<TenantsResponse, Error>({
    queryKey: ["tenants", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      tenantService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const tenants: Tenant[] = query.data?.pages.flatMap((p) => p.tenants) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...mutations,
    tenants,
    total,
    isLoading: query.isLoading || mutations.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
